import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'

import Autocomplete from '@mui/material/Autocomplete'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import TextInput from '@/components/ui/inputs/TextInput'
import TextField from '@/components/formik/fields/TextField'
import type { AvailabilityWizardValues } from '../CreateAvailabilityWizard'

type SpecialitiesStepProps = {
  specialities: string[]
}

export default function SpecialitiesStep({ specialities }: SpecialitiesStepProps) {
  const { t } = useTranslation()
  const { values, setFieldValue, touched, errors } = useFormikContext<AvailabilityWizardValues>()

  const hasError = !!(touched.specialities && errors.specialities)

  return (
    <Stack spacing={3}>
      <Stack spacing={0.5}>
        <Typography variant="h6">
          {t('page.meetingLink.wizard.specialities.title', 'What specialities?')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('page.meetingLink.wizard.specialities.subtitle', 'Select which services patients can book')}
        </Typography>
      </Stack>

      <Autocomplete
        multiple
        freeSolo
        data-testid="availability-wizard-specialities"
        options={specialities}
        value={values.specialities || []}
        onChange={(_event, newValue) => {
          setFieldValue('specialities', newValue)
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index })
            return (
              <Chip
                key={key}
                label={option}
                color="primary"
                variant="filled"
                size="small"
                {...tagProps}
              />
            )
          })
        }
        renderInput={(params) => (
          <TextInput
            {...params}
            label={t('page.meetingLink.wizard.specialities.inputLabel', 'Specialities')}
            placeholder={
              (values.specialities?.length ?? 0) === 0
                ? t('page.meetingLink.wizard.specialities.placeholder', 'Search or type to add...')
                : ''
            }
            error={hasError}
            helperText={
              hasError
                ? (errors.specialities as string)
                : t('page.meetingLink.wizard.specialities.inputHelp', 'Select from the list or type a new one and press Enter')
            }
          />
        )}
      />

      <TextField
        fullWidth
        label={t('field.avgMeetingDuration.label')}
        name="avgMeetingDuration"
        type="number"
        inputProps={{ min: 1, inputMode: 'numeric', 'aria-label': t('field.avgMeetingDuration.label'), 'data-testid': 'availability-wizard-duration' }}
        helperText={t('page.meetingLink.wizard.specialities.durationHelp', 'How long does a typical appointment take? (minutes)')}
      />
    </Stack>
  )
}
